import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ChevronRight, Building2 } from "lucide-react";

const HOTEL_BG = "/room-home.jpg";

export default function Login() {
  const { user, login } = useAuth();
  const { setProperty } = useProperty();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) {
    return <Navigate to={user.role === "supervisor" ? "/inspect" : "/dashboard"} replace />;
  }

  const doLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/lookup", { email: email.trim().toLowerCase() });
      if (!data.exists) {
        toast.error("No account found for this email. Contact your admin.");
        return;
      }
      setLookup(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password, lookup?.property?.id);
      if (u.property) setProperty(u.property);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "supervisor" ? "/inspect" : "/dashboard");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--linen)]">
      <div className="hidden md:block relative">
        <img src={HOTEL_BG} alt="Tamanna Hotels" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1A2C]/85 via-[#0F1A2C]/70 to-[#0F1A2C]/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white/10 backdrop-blur border border-white/20 text-[var(--gold)] font-display font-bold text-2xl flex items-center justify-center">T</div>
            <div>
              <div className="hotel-overline text-[var(--gold)] tracking-hotel">Tamanna Hotels Group</div>
              <div className="font-display text-xl">Housekeeping Checklist</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="hotel-overline text-[var(--gold)] mb-3">
              {lookup?.property ? lookup.property.code : "5 Properties · One Standard"}
            </div>
            <h1 className="font-display text-5xl leading-[1.05] mb-4">
              {lookup?.property ? lookup.property.tagline : "Immaculate rooms, one checklist at a time."}
            </h1>
            <p className="text-white/70 text-base leading-relaxed">
              {lookup?.property
                ? `${lookup.property.subtitle}. Sign in to open your housekeeping checklist.`
                : "Enter your admin-issued email to open your hotel's housekeeping checklist. English · हिंदी · मराठी supported."}
            </p>
          </div>
          <div className="text-xs text-white/50 font-mono-hotel">
            {lookup?.property ? lookup.property.name : "Grand · Premier · Bizz · Executive · Imperial"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 paper-grain">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8">
            <div className="hotel-overline mb-2">Secure Sign In</div>
            <h2 className="font-display text-4xl text-[var(--navy)]">Welcome.</h2>
            <p className="text-[var(--ink-2)] mt-2">Enter your email — your hotel's checklist opens automatically.</p>
          </div>

          {!lookup ? (
            <form onSubmit={doLookup} className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Email</Label>
                <div className="relative mt-2">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="you@tamannahotels.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-white border-[var(--border-warm)] focus-visible:ring-[var(--gold)]"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="email-continue-button"
                className="w-full h-12 bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold tracking-wide rounded-md"
              >
                {loading ? "Checking…" : (<>Continue <ChevronRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            </form>
          ) : (
            <form onSubmit={doLogin} className="space-y-5">
              <div
                data-testid="lookup-property-badge"
                className="flex items-center gap-3 bg-[var(--gold-light)] border border-[var(--gold)]/40 rounded-md p-4"
              >
                <div className="w-10 h-10 rounded-md bg-[var(--navy)] text-[var(--gold)] flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[var(--navy)]">{lookup.name}</div>
                  <div className="text-xs text-[var(--ink-2)]">
                    {lookup.property ? lookup.property.name : "Tamanna Hotels Group — All Properties"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setLookup(null); setPassword(""); }}
                  className="text-xs text-[var(--gold)] hover:underline"
                >
                  Change
                </button>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Password</Label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 bg-white border-[var(--border-warm)] focus-visible:ring-[var(--gold)]"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-12 bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold tracking-wide rounded-md"
              >
                {loading ? "Signing in…" : (<><ShieldCheck className="w-4 h-4 mr-2" /> Sign In</>)}
              </Button>
            </form>
          )}

          <p className="text-xs text-[var(--muted)] mt-8 text-center leading-relaxed">
            Login is provided by the Tamanna Hotels group admin only.<br />
            No self-registration. Contact <span className="font-mono-hotel">prathveesh.shetty@tamannahotels.com</span> for access.
          </p>
        </div>
      </div>
    </div>
  );
}
